// apiServiceCompany.js
import axios from 'axios';
import logger from '../utils/logger';
import { createHeaders } from './headerUtils';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const apiServiceCompany = {
  getCompany: async (companyid) => {
    try {
      const storedCompanyData = JSON.parse(localStorage.getItem('companyData'));

      if (storedCompanyData && (companyid === storedCompanyData.data['_id'])) {
        logger.debug("apiServiceCompany (OK): getCompany - Company already cached");
        return storedCompanyData;
      }

      const apiUrl = `${BASE_URL}/company/${companyid}`;
      const headers = createHeaders();
      logger.debug("apiServiceCompany (ok): getCompany ", `Querying: ${apiUrl}`);
      const response = await axios.get(apiUrl, { headers });
      logger.debug("apiServiceCompany (ok): getCompany Response", response);
      return response.data;
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): getCompany ", error)
      return {"status":"fail", "data":error.response.data.data};
    }
  },

  findCompany: async (searchterm) => {
    try {
      const apiUrl = `${BASE_URL}/company/${searchterm}`;
      const headers = createHeaders();
      const reqbody = { restore: searchterm };
      const response = await axios.post(apiUrl, reqbody, { headers });
      return response.data;
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): findCompany ", error.message)
      return {"status":"fail", "data":error.message};
    }
  },

  getCompanyUserPermissions: async (companyid, userid) => {
    try {
      const apiUrl = `${BASE_URL}/company/detail/${companyid}/user/${userid}/permissions`;
      const headers = createHeaders();
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): getCompanyUserPermissions ", error.message)
      throw error;
    }
  },

  getCompanyEntitlements: async (companyid) => {
    try {
      const apiUrl = `${BASE_URL}/company/detail/${companyid}/entitlements/all`;
      const headers = createHeaders();
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): getCompanyEntitlements ", error.message)
      throw error;
    }
  },

  getCompanyEntitlementsDescr: async () => {
    try {
      const apiUrl = `${BASE_URL}/company/detail/entitlements/describe`;
      const headers = createHeaders();
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): getCompanyEntitlementsDescr ", error.message)
      throw error;
    }
  },

  getUser: async (email) => {
    try {
      const apiUrl = `${BASE_URL}/company/detail/user/${email}`;
      const headers = createHeaders();
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): getUser ", error.message)
      throw error;
    }
  },

  getCompanyUsers: async (companyid, options = {}) => {
    try {
      let allUsers = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const apiUrl = `${BASE_URL}/company/detail/${companyid}/users`;
        const headers = createHeaders();
        const response = await axios.get(apiUrl, { 
          headers,
          params: { page: currentPage }
        });

        const pageData = response.data.data;
        allUsers = [...allUsers, ...pageData];
        hasMore = response.data.pagination?.hasNextPage;
        currentPage++;

        logger.debug(`Fetched page ${currentPage-1}, got ${pageData.length} users. Total so far: ${allUsers.length}`);
      }
      const users = allUsers.map(user => ({
        ...user,
        isInternalUser: user.user.email?.includes('@meltwater.com') || user.user.isInternal
      }));

      return {
        status: "ok",
        data: users,
        metadata: {
          total: users.length,
          internal: users.filter(u => u.isInternalUser).length,
          external: users.filter(u => !u.isInternalUser).length
        },
        pagination: {
          currentPage,
          totalPages: currentPage - 1,
          totalCount: users.length
        }
      };
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): getCompanyUsers ", error);
      throw error;
    }
  },

// this function, similar to getCompanyUsers, fetches all users from the company but with worksapaces they are part of
  getUsersData: async (companyid, eventType = null) => {
    try {
        const apiUrl = `${BASE_URL}/company/detail/${companyid}/users-data`;
        const headers = createHeaders();
        const params = eventType ? { eventType } : {};
        const response = await axios.get(apiUrl, { headers, params });
        
        // Log the operation type for debugging
        logger.debug(`apiServiceCompany: getUsersData (${eventType || 'view'})`);
        
        return response.data;
    } catch (error) {
        logger.error("apiServiceCompany (ERROR): getUsersData ", error.message);
        throw error;
    }
  },

  trackExportOperation: async (companyid, trackingData) => {
    try {
        const apiUrl = `${BASE_URL}/company/detail/${companyid}/track-export`;
        const headers = createHeaders();
        await axios.post(apiUrl, trackingData, { headers });
    } catch (error) {
        logger.error("Export tracking error:", error);
        // Don't throw - we don't want to block the export if tracking fails
    }
  },

  getCompanyInfo: async (companyid) => {
    try {
      const apiUrl = `${BASE_URL}/company/${companyid}`;
      const headers = createHeaders();
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): getCompanyInfo ", error.message);
      throw error;
    }
  },

  getWorkspaceData: async (companyid, eventType = null) => {
    try {
        const apiUrl = `${BASE_URL}/company/detail/${companyid}/workspace-data`;
        const headers = createHeaders();
        const params = eventType ? { eventType } : {};
        const response = await axios.get(apiUrl, { headers, params });
        
        logger.debug(`apiServiceCompany: getWorkspaceData (${eventType || 'view'})`);
        
        return response.data;
    } catch (error) {
        logger.error("apiServiceCompany (ERROR): getWorkspaceData ", error.message);
        throw error;
    }
  },

  getCompanyOverrides: async (companyid) => {
    try {
      const apiUrl = `${BASE_URL}/company/detail/${companyid}/overrides`;
      const headers = createHeaders();
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): getCompanyOverrides ", error.message);
      throw error;
    }
  },

  getCompanyOverridesHistory: async (companyid, page = 1, pageSize = 25) => {
    try {
      const apiUrl = `${BASE_URL}/company/detail/${companyid}/overrides/history`;
      const headers = createHeaders();
      const response = await axios.get(apiUrl, { headers, params: { page, pageSize } });
      return response.data;
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): getCompanyOverridesHistory ", error.message);
      throw error;
    }
  },

  updateCompanyOverrides: async (companyid, overrides) => {
    try {
      const apiUrl = `${BASE_URL}/company/detail/${companyid}/overrides`;
      const headers = createHeaders();
      const response = await axios.put(apiUrl, { overrides }, { headers });
      return response.data;
    } catch (error) {
      logger.error("apiServiceCompany (ERROR): updateCompanyOverrides ", error.message);
      throw error;
    }
  },
};

export default apiServiceCompany;
